import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box } from "@mui/material";
import { observer } from "mobx-react";
import dayjs from "dayjs";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { DateRangePickerNew } from "../../../../../views/components/common/textFields/DatePickerRangeNew";
import {
  SWAYY_CHANNEL_OPTIONS,
  SWAYY_ORDER_STATUS_TABS,
} from "../../../../../models/SwayyOrder/SwayyOrderFilters";
import { SwayyOrder } from "../../../../../models/SwayyOrder/SwayyOrder";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import SwayyOrdersTable from "../../components/orders/SwayyOrdersTable";
import { SwayyPageHeader } from "../../components/SwayyPageShell";
import SwayyStatusTabs, {
  SWAYY_ORDER_STATUS_TAB_ICONS,
} from "../../components/SwayyStatusTabs";
import SwayySearchFiltersPanel, {
  SwayyFilterDateField,
  SwayyFilterDropdownField,
  SwayyFilterSearchField,
  SwayyFilterSearchInput,
  SWAYY_FILTER_DROPDOWN_DEFAULTS,
} from "../../components/filters/SwayySearchFiltersPanel";
import { useStores } from "../../../use-stores";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { userStore } from "../../../../../stores/sharedStores";
import { SwayyEmployeeRoles } from "../../../../../models/Employees/SwayyEmployeeRoles";

const SEARCH_DEBOUNCE_MS = 400;

const SwayyOrders = () => {
  const { swayyOrderStore: ordersStore } = useStores();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showError } = useNotification();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipFirstSearchEffect = useRef(true);
  const [urlReady, setUrlReady] = useState(false);

  useEffect(() => {
    if (
      !userStore.hasSwayyPermission([
        SwayyEmployeeRoles.VIEW_ORDERS,
        SwayyEmployeeRoles.EDIT_ORDERS,
      ])
    ) {
      return;
    }

    ordersStore.initializeParamsFromSearch(searchParams);
    setUrlReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      !urlReady ||
      !userStore.hasSwayyPermission([
        SwayyEmployeeRoles.VIEW_ORDERS,
        SwayyEmployeeRoles.EDIT_ORDERS,
      ])
    ) {
      return;
    }

    const nextParams = ordersStore.buildSearchParams();
    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    urlReady,
    ordersStore.params.page,
    ordersStore.params.limit,
    ordersStore.params.q,
    ordersStore.params.status,
    ordersStore.params.payment_status,
    ordersStore.params.channel,
    ordersStore.params.sort_by,
    ordersStore.params.sort_order,
    ordersStore.params.start_date,
    ordersStore.params.end_date,
  ]);

  useEffect(() => {
    if (
      !urlReady ||
      !userStore.hasSwayyPermission([
        SwayyEmployeeRoles.VIEW_ORDERS,
        SwayyEmployeeRoles.EDIT_ORDERS,
      ])
    ) {
      return;
    }

    const loadOrders = async () => {
      try {
        await ordersStore.getOrders();
      } catch (_error) {
        showError("Error", "Failed to load orders.");
      }
    };
    loadOrders();
  }, [
    urlReady,
    ordersStore.params.page,
    ordersStore.params.limit,
    ordersStore.params.q,
    ordersStore.params.status,
    ordersStore.params.payment_status,
    ordersStore.params.channel,
    ordersStore.params.sort_by,
    ordersStore.params.sort_order,
    ordersStore.params.start_date,
    ordersStore.params.end_date,
  ]);

  useEffect(() => {
    if (
      !urlReady ||
      !userStore.hasSwayyPermission([
        SwayyEmployeeRoles.VIEW_ORDERS,
        SwayyEmployeeRoles.EDIT_ORDERS,
      ])
    ) {
      return;
    }

    if (skipFirstSearchEffect.current) {
      skipFirstSearchEffect.current = false;
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      ordersStore.applySearch();
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [urlReady, ordersStore.searchDraft]);

  useEffect(() => {
    if (
      !userStore.hasSwayyPermission([
        SwayyEmployeeRoles.VIEW_ORDERS,
        SwayyEmployeeRoles.EDIT_ORDERS,
      ])
    ) {
      return;
    }
    if (ordersStore.error) {
      showError("Error", ordersStore.error);
    }
  }, [ordersStore.error]);

  const handleClearSearch = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    ordersStore.setSearchDraft("");
    if (ordersStore.params.q) {
      ordersStore.setParams({ q: "", page: 1 });
    }
  };

  const handleClearAll = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    ordersStore.clearFilters();
  };

  const handleApply = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    ordersStore.applySearch();
    ordersStore.applyFilters();
  };

  const handleRefresh = async () => {
    try {
      await ordersStore.getOrders();
    } catch (_error) {
      showError("Error", "Failed to load orders.");
    }
  };

  const handleViewOrder = (order: SwayyOrder) => {
    navigate(`/orders/${order.id}`);
  };

  if (
    !userStore.hasSwayyPermission([
      SwayyEmployeeRoles.VIEW_ORDERS,
      SwayyEmployeeRoles.EDIT_ORDERS,
    ])
  ) {
    return <PermissionBlockLabel />;
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 } }}>
      <SwayyPageHeader
        title="Orders"
        description="Track and manage Swayy orders across channels."
      />

      <SwayySearchFiltersPanel
        open={ordersStore.filtersOpen}
        onToggle={ordersStore.toggleFiltersOpen}
        onClear={handleClearAll}
        onApply={handleApply}
      >
        <SwayyFilterSearchField>
          <SwayyFilterSearchInput
            value={ordersStore.searchDraft}
            onChange={ordersStore.setSearchDraft}
            onClear={handleClearSearch}
            placeholder="Search order no, customer, email, phone..."
            onEnter={() => {
              if (debounceRef.current) clearTimeout(debounceRef.current);
              ordersStore.applySearch();
            }}
          />
        </SwayyFilterSearchField>
        <SwayyFilterDropdownField>
          <AppSearchDropdownMulti
            title="Select Channel"
            isMultiSelect={true}
            list={SWAYY_CHANNEL_OPTIONS}
            value={ordersStore.draftChannel.map((id) => {
              const item = SWAYY_CHANNEL_OPTIONS.find(
                (option) => option.id === id,
              );
              return item
                ? { id: item.id, value: item.value }
                : { id, value: id };
            })}
            {...SWAYY_FILTER_DROPDOWN_DEFAULTS}
            handleSelect={(items) => {
              ordersStore.setDraftChannel(items.map((item) => item.id));
            }}
          />
        </SwayyFilterDropdownField>
        <SwayyFilterDateField>
          <DateRangePickerNew
            placeholder="Date Range"
            startDate={
              ordersStore.draftStartDate
                ? dayjs(ordersStore.draftStartDate)
                : null
            }
            endDate={
              ordersStore.draftEndDate ? dayjs(ordersStore.draftEndDate) : null
            }
            onRangeSelect={(range) => {
              ordersStore.setDraftDateRange(
                range.startDate ? range.startDate.format("YYYY-MM-DD") : "",
                range.endDate ? range.endDate.format("YYYY-MM-DD") : "",
              );
            }}
          />
        </SwayyFilterDateField>
      </SwayySearchFiltersPanel>

      <SwayyStatusTabs
        value={ordersStore.statusTab}
        onChange={ordersStore.setStatusTab}
        tabs={SWAYY_ORDER_STATUS_TABS}
        icons={SWAYY_ORDER_STATUS_TAB_ICONS}
      />

      <SwayyOrdersTable onViewOrder={handleViewOrder} onRetry={handleRefresh} />
    </Box>
  );
};

export default observer(SwayyOrders);
